import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { OccurrenceResponse } from '@tabita/contracts';
import { formatDate } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { calendarApi } from '@/services/calendar.api';
import { Button, Spinner } from '@/components/ui';
import { buildMonthGrid, toDateKey } from './calendar-dates';

/** Reached from the Calendar page's "Print" link, which passes the same
 *  42-day grid range and month label it is currently showing, so this
 *  prints exactly the month the operator was looking at. */
export function PrintCalendarPage() {
  const { t, locale } = useI18n();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const monthLabel = searchParams.get('month') ?? '';
  const [occurrences, setOccurrences] = useState<readonly OccurrenceResponse[] | null>(null);

  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setOccurrences(null);

    async function fetchOccurrences(): Promise<void> {
      const data = await calendarApi.listOccurrences(from, to);
      if (!cancelled) setOccurrences(data);
    }

    void fetchOccurrences();
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  useEffect(() => {
    if (occurrences) window.print();
  }, [occurrences]);

  const days = from ? buildMonthGrid(new Date(`${from}T00:00:00.000Z`)) : [];
  const currentMonth = from ? new Date(`${from}T00:00:00.000Z`).getUTCMonth() : 0;

  const byDate = new Map<string, OccurrenceResponse[]>();
  for (const occurrence of occurrences ?? []) {
    const key = toDateKey(occurrence.startAt);
    const bucket = byDate.get(key);
    if (bucket) bucket.push(occurrence);
    else byDate.set(key, [occurrence]);
  }

  return (
    <div className="print-page">
      <div className="print-page-toolbar">
        <Link to="/calendar" className="back-link">
          {t('common.actions.back')}
        </Link>
        <Button type="button" variant="primary" onClick={() => window.print()}>
          {t('common.actions.print')}
        </Button>
      </div>

      <h1 className="print-page-title">{t('calendar.title')}</h1>
      <p className="print-page-meta">{monthLabel}</p>

      {!occurrences && <Spinner label={t('common.states.loading')} />}

      {occurrences && (
        <div className="calendar-print-grid">
          {days.map((day) => {
            const key = toDateKey(day);
            const dayOccurrences = byDate.get(key) ?? [];
            const inMonth = day.getUTCMonth() === currentMonth;

            return (
              <div
                key={key}
                className="calendar-print-cell"
                data-outside-month={!inMonth || undefined}
              >
                <span className="calendar-print-day-number">{day.getUTCDate()}</span>
                {dayOccurrences.map((occurrence) => (
                  <p key={occurrence.id} className="calendar-print-entry">
                    {!occurrence.isAllDay && (
                      <span>
                        {formatDate(
                          occurrence.startAt,
                          locale,
                          { hour: 'numeric', minute: '2-digit' },
                          'UTC',
                        )}{' '}
                      </span>
                    )}
                    {occurrence.title}
                  </p>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
